import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import graphData from '../data/Graph.json';
import './historyGraph.css';

// ── Force-simulation tuning ─────────────────────────────────────────────────
const REPULSE_STRENGTH = 5200;
const EDGE_DISTANCE = 130;
const EDGE_STIFFNESS = 0.028;
const CENTRE_PULL = 0.00025;
const DAMPING = 0.74;
const ALPHA_DECAY = 0.022;
const ALPHA_MIN = 0.004;         // floor — barely breathing, never fully dead
const ALPHA_KICK = 0.45;         // bumped on interaction
const SETTLE_ITERATIONS = 240;   // pre-settle ticks at mount (no paint)
const COLLISION_BUFFER = 10;     // minimum gap between node edges (px)
const COLLISION_ITERS = 2;       // position-correction passes per tick

// Pair-wise repulsion multipliers — gentle bias so jobs don't clump on top
// of each other, but the layout is mostly driven by edge attraction so each
// job becomes the centre of its own cluster of attached skills/tools.
const REPULSE_PAIR = {
  'job-job': 2.6,
  'job-skill': 1.1,
  'job-tool': 1.0,
  'skill-skill': 0.85,
  'skill-tool': 0.75,
  'tool-tool': 0.65,
};
const pairKey = (a, b) => (a < b ? `${a}-${b}` : `${b}-${a}`);

// ── Job anchor positions ────────────────────────────────────────────────────
// Pinned chronologically along the bottom row so the graph reads left→right
// like a career timeline. Cinimod (freelance, parallel) and Side Projects
// (ongoing, personal) sit on a top tier above the main career line.
// No job pins — graph is now skill-set-only. Kept the constant so the init
// effect's `JOB_PINS[id]` lookup still resolves cleanly to undefined.
const JOB_PINS = {};

// ── Core-skill anchor positions ─────────────────────────────────────────────
// A handful of skills connect to almost every job ("super-hubs"). Left to the
// physics they get tugged toward every job's anchor at once and end up bunched
// in the middle, dragging their tools with them. Pinning them across a band
// between the two job rows turns them into stable structural anchors that
// the rest of the cluster drapes around.
// Anchor positions for every cluster hub. CORE in the middle; the rest fan
// out so big clusters get more room and related clusters sit near each other.
// Layout (rough): visual creation along the top, AI / sensing along the
// bottom-centre, networking + web on the right, leadership/photo on the left.
const SKILL_PINS = {
  'real-time-3d':        { x:    0, y:    0 },  // CORE
  '3d-modelling':        { x: -260, y: -110 },
  'interaction-systems': { x:  340, y:  -80 },  // biggest cluster — slight extra room
  'neural-nets':         { x:  140, y:  200 },
  'computer-vision':     { x: -150, y:  200 },
  'networking':          { x:  400, y:  180 },
  'web-dev':             { x:  540, y:   50 },
  'leadership':          { x: -420, y:   80 },
  'photography':         { x: -540, y: -220 },
};

// ── Cluster colouring ───────────────────────────────────────────────────────
// Multi-source BFS from these hubs assigns every node to the colour of its
// closest hub. Order matters in ties — earlier in this list wins. Pick the
// strongest semantic anchors so each cluster reads as one domain.
const CLUSTER_HUBS = [
  'real-time-3d',
  '3d-modelling',
  'neural-nets',
  'computer-vision',
  'networking',
  'web-dev',
  'interaction-systems',
  'leadership',
  'photography',
];

// Editorial palette — distinct hues at a darker, less saturated register so
// the cluster colours sit alongside the cream/ink/persimmon site identity
// instead of fighting it. CORE keeps the site's persimmon accent.
const CLUSTER_COLORS = {
  'real-time-3d':        '#b86a37',  // burnt persimmon (CORE on brand)
  '3d-modelling':        '#3c5879',  // muted navy
  'neural-nets':         '#b8533d',  // brick red (PyTorch nod, less neon)
  'computer-vision':     '#3d7373',  // dusty teal
  'networking':          '#555d96',  // muted indigo
  'web-dev':             '#9c7a2c',  // antique gold
  'interaction-systems': '#925670',  // muted rose
  'leadership':          '#3d4350',  // graphite
  'photography':         '#5a7a55',  // moss
};

const BASE_RADIUS = { job: 38, skill: 19, tool: 16 };
const RADIUS_PER_EDGE = { job: 0, skill: 1.4, tool: 0.9 };
const RADIUS_MAX = { job: 38, skill: 32, tool: 26 };

// ── Build adjacency / degree from edges ─────────────────────────────────────
function buildIndex(nodes, edges) {
  const byId = new Map();
  nodes.forEach(n => byId.set(n.id, n));
  const adj = new Map();
  nodes.forEach(n => adj.set(n.id, new Set()));
  const validEdges = [];
  edges.forEach(e => {
    // Tolerate section-header strings sprinkled into the edges array.
    if (!Array.isArray(e) || e.length < 2) return;
    const [a, b] = e;
    if (!byId.has(a) || !byId.has(b)) return;
    adj.get(a).add(b);
    adj.get(b).add(a);
    validEdges.push([a, b]);
  });
  return { byId, adj, edges: validEdges };
}

function nodeRadius(node, degree) {
  const base = BASE_RADIUS[node.type] ?? 14;
  const grow = (RADIUS_PER_EDGE[node.type] ?? 0) * degree;
  const max = RADIUS_MAX[node.type] ?? 30;
  return Math.min(base + grow, max);
}

// ── Glyph registry ──────────────────────────────────────────────────────────
// Icons are designed in a 24-unit box centred at (0, 0). Stroke scales with
// the node — see `.hg-glyph` (no non-scaling-stroke) and the per-node scale.
const GLYPHS = {
  // Isometric cube outline + visible front edges
  cube: <>
    <path d="M0 -9 L8 -4.5 L8 4.5 L0 9 L-8 4.5 L-8 -4.5 Z" />
    <path d="M-8 -4.5 L0 0 L8 -4.5 M0 0 L0 9" />
  </>,
  // Lightning bolt
  bolt: <path d="M3 -10 L-5 1 L-1 1 L-3 10 L5 -1 L1 -1 Z" />,
  // VR goggles — body + two lenses
  glasses: <>
    <path d="M-9 -2 Q-9 -6 -6 -6 L6 -6 Q9 -6 9 -2 L9 3 Q9 6 6 6 L4 6 L0 2 L-4 6 L-6 6 Q-9 6 -9 3 Z" />
    <circle cx="-4" cy="0" r="2" />
    <circle cx="4" cy="0" r="2" />
  </>,
  // Camera with lens + viewfinder bump
  camera: <>
    <path d="M-3 -6 L-2 -8 L2 -8 L3 -6 L7 -6 Q9 -6 9 -4 L9 5 Q9 7 7 7 L-7 7 Q-9 7 -9 5 L-9 -4 Q-9 -6 -7 -6 Z" />
    <circle cx="0" cy="1" r="3.5" />
  </>,
  // Sun — disc + 8 rays
  sun: <>
    <circle r="3.5" />
    <path d="M0 -6.5 L0 -9 M0 6.5 L0 9 M-6.5 0 L-9 0 M6.5 0 L9 0 M-4.6 -4.6 L-6.4 -6.4 M4.6 4.6 L6.4 6.4 M-4.6 4.6 L-6.4 6.4 M4.6 -4.6 L6.4 -6.4" />
  </>,
  // Pointer / cursor arrow
  cursor: <path d="M-5 -8 L-5 6 L-1.5 2.5 L1 8 L4 7 L1.5 1.5 L6 1 Z" />,
  // Building — outline + simple windows + door
  building: <>
    <rect x="-7" y="-8" width="14" height="16" />
    <rect x="-4.5" y="-5" width="2.5" height="2.5" />
    <rect x="-1.25" y="-5" width="2.5" height="2.5" />
    <rect x="2" y="-5" width="2.5" height="2.5" />
    <rect x="-4.5" y="-1" width="2.5" height="2.5" />
    <rect x="2" y="-1" width="2.5" height="2.5" />
    <path d="M-1.5 8 L-1.5 3 L1.5 3 L1.5 8" />
  </>,
  // Lightbulb — bulb + base lines
  bulb: <>
    <path d="M-5 -2 Q-5 -9 0 -9 Q5 -9 5 -2 Q5 1 3 3 L3 5 L-3 5 L-3 3 Q-5 1 -5 -2 Z" />
    <path d="M-2.5 7.5 L2.5 7.5 M-1.5 9.5 L1.5 9.5" />
  </>,
  // Stacked layers — three diamonds
  layers: <>
    <path d="M-8 -3 L0 -7 L8 -3 L0 1 Z" />
    <path d="M-8 1.5 L0 -2.5 L8 1.5 L0 5.5 Z" />
    <path d="M-8 6 L0 2 L8 6 L0 10 Z" />
  </>,
  // Two people — front figure + half figure behind
  people: <>
    <circle cx="-2" cy="-4" r="3" />
    <path d="M-8 8 L-8 4.5 Q-8 1 -2 1 Q4 1 4 4.5 L4 8" />
    <circle cx="5" cy="-3" r="2.4" />
    <path d="M2 5 Q2 3 5 3 Q9 3 9 5.5 L9 8" />
  </>,
  // Neural-net topology: 2-3-2 layers
  network: <>
    <circle cx="-7" cy="-5" r="1.7" />
    <circle cx="-7" cy="5" r="1.7" />
    <circle cx="0" cy="-7" r="1.7" />
    <circle cx="0" cy="0" r="1.7" />
    <circle cx="0" cy="7" r="1.7" />
    <circle cx="7" cy="-4" r="1.7" />
    <circle cx="7" cy="4" r="1.7" />
    <path d="M-7 -5 L0 -7 M-7 -5 L0 0 M-7 -5 L0 7 M-7 5 L0 -7 M-7 5 L0 0 M-7 5 L0 7 M0 -7 L7 -4 M0 0 L7 -4 M0 0 L7 4 M0 7 L7 4" />
  </>,
  // Code brackets with slash between
  brackets: <>
    <path d="M-3 -8 L-8 0 L-3 8" />
    <path d="M3 -8 L8 0 L3 8" />
    <path d="M2 -8 L-2 8" />
  </>,
  // Game controller — body, d-pad, two face buttons
  controller: <>
    <path d="M-9 -2 Q-9 -5 -6 -5 L-3 -5 Q0 -5 0 -2 Q0 -5 3 -5 L6 -5 Q9 -5 9 -2 L9 3 Q9 6 6 6 Q4 6 3 4 L-3 4 Q-4 6 -6 6 Q-9 6 -9 3 Z" />
    <path d="M-7 0 L-3 0 M-5 -2 L-5 2" />
    <circle cx="3.5" cy="0" r="1" />
    <circle cx="6.5" cy="0" r="1" />
  </>,
  // Shopping cart
  cart: <>
    <path d="M-9 -5 L-6 -5 L-3.5 5 L7 5" />
    <path d="M-4.5 -1 L8 -1 L7 5" />
    <circle cx="-2" cy="8" r="1.4" />
    <circle cx="5.5" cy="8" r="1.4" />
  </>,
  // 5-point star
  star: <path d="M0 -9 L2.6 -3 L9 -2.4 L4 2.2 L5.5 9 L0 5.5 L-5.5 9 L-4 2.2 L-9 -2.4 L-2.6 -3 Z" />,
  // Three sine waves
  waves: <>
    <path d="M-9 -4 Q-6 -7 -3 -4 T3 -4 T9 -4" />
    <path d="M-9 0 Q-6 -3 -3 0 T3 0 T9 0" />
    <path d="M-9 4 Q-6 1 -3 4 T3 4 T9 4" />
  </>,
  // Flame
  flame: <path d="M0 9 Q-6 7 -6 1 Q-6 -3 -2 -7 Q-1 -4 1 -6 Q3 -8 2 -10 Q7 -7 7 -1 Q7 7 0 9 Z" />,
  // Play button in rounded rectangle (video / YouTube)
  play: <>
    <rect x="-9" y="-6" width="18" height="12" rx="2.5" />
    <path d="M-2 -3 L-2 3 L4 0 Z" />
  </>,
  // Browser window with title-bar dots
  browser: <>
    <rect x="-9" y="-7" width="18" height="14" rx="1.5" />
    <path d="M-9 -2.5 L9 -2.5" />
    <circle cx="-7" cy="-5" r="0.7" />
    <circle cx="-5" cy="-5" r="0.7" />
    <circle cx="-3" cy="-5" r="0.7" />
  </>,
  // Sparkles / explosion (Niagara FX)
  sparks: <>
    <path d="M0 -9 L0 -3 M0 3 L0 9 M-9 0 L-3 0 M3 0 L9 0" />
    <path d="M-6.4 -6.4 L-3.2 -3.2 M3.2 3.2 L6.4 6.4 M-6.4 6.4 L-3.2 3.2 M3.2 -3.2 L6.4 -6.4" />
    <circle r="1.5" />
  </>,
  // Git branch graph
  gitbranch: <>
    <circle cx="-5" cy="-6" r="1.7" />
    <circle cx="-5" cy="6" r="1.7" />
    <circle cx="5" cy="0" r="1.7" />
    <path d="M-5 -4.3 L-5 4.3" />
    <path d="M-5 -3 Q-5 0 0 0 L3.3 0" />
  </>,
  // Sphere with stripes (sculpting / DCC)
  sphere: <>
    <circle r="7.5" />
    <path d="M-7 -2 Q0 1 7 -2 M-7 3 Q0 6 7 3" />
  </>,
  // Mountain range landscape (environment art)
  landscape: <>
    <path d="M-9 7 L-3 -3 L1 1 L4 -5 L9 7 Z" />
    <path d="M-9 7 L9 7" />
    <circle cx="-5" cy="-4" r="1.5" />
  </>,
  // Flowing hair strands
  hair: <>
    <path d="M-7 -8 Q-9 0 -5 9" />
    <path d="M-3 -9 Q-5 0 -2 9" />
    <path d="M1 -9 Q-1 0 2 9" />
    <path d="M5 -8 Q7 0 5 9" />
  </>,
  // Rocket / side-projects
  rocket: <>
    <path d="M0 -10 Q4 -7 4 -1 L4 4 L-4 4 L-4 -1 Q-4 -7 0 -10 Z" />
    <circle cy="-3" r="1.6" />
    <path d="M-4 1 L-7 5 L-4 4 M4 1 L7 5 L4 4" />
    <path d="M-2 4 L-2 8 M2 4 L2 8 M0 4 L0 9" />
  </>,
};

// id → glyph key. Multiple ids can share an icon when conceptually related.
const GLYPH_FOR = {
  // skills
  'real-time-3d': 'bolt',
  '3d-modelling': 'cube',
  'vr': 'glasses',
  'ar': 'glasses',
  'photogrammetry': 'camera',
  'optimisation': 'bolt',
  'pipelines': 'layers',
  'pipelines-bp': 'layers',
  'lighting-shading': 'sun',
  'permanent-installs': 'building',
  'prototyping': 'bulb',
  'rnd': 'bulb',
  'software-arch': 'layers',
  'leadership': 'people',
  'hiring': 'people',
  'neural-nets': 'network',
  'ai-pipelines': 'network',
  'touch-tooling': 'cursor',
  'depth-sensing': 'waves',
  'game-design': 'controller',
  'composition': 'camera',
  'online-pipeline': 'cart',
  'big-brand': 'star',
  // tools
  'unreal': 'cube',
  'unity': 'cube',
  'touchdesigner': 'network',
  'pytorch': 'flame',
  'onnx': 'network',
  'python': 'brackets',
  'cpp': 'brackets',
  'csharp': 'brackets',
  'blueprint-vs': 'brackets',
  'opengl': 'cube',
  'niagara': 'sparks',
  'react': 'brackets',
  'javascript': 'brackets',
  'youtube': 'play',
  'n8n': 'sparks',
  'web-dev': 'browser',
  'hair-grooming': 'hair',
  'environment-art': 'landscape',
  'mentoring': 'people',
  'scoping': 'bulb',
  'maya': 'sphere',
  'blender': 'sphere',
  'zbrush': 'sphere',
  'substance': 'cube',
  'hlsl': 'sun',
  'threejs': 'cube',
  'git': 'gitbranch',
  'interaction-systems': 'controller',
  'interaction-design': 'cursor',
  'anim-state-machines': 'waves',
  'behaviour-trees': 'gitbranch',
  'behaviour-trees-ue': 'gitbranch',
  'state-machines': 'network',
  'realitycapture': 'camera',
  'meshroom': 'camera',
  'kinect': 'waves',
  'dmx-net': 'bolt',
  'networking': 'sparks',
  'websocket': 'browser',
  'websocket-web': 'browser',
  'udp': 'waves',
  'tcp': 'gitbranch',
  'vulkan': 'cube',
  'directx': 'cube',
  'directx-unity': 'cube',
  'opengl-unity': 'cube',
  'python-ue': 'brackets',
  'leap-motion-bu': 'cursor',
  'speech-rec-bu': 'waves',
  'blueprint-vs-sm': 'brackets',
  'materials': 'sphere',
  'text-to-voice': 'play',
  'voice-cloning': 'waves',
  'computer-vision': 'glasses',
  'skeletal-tracking': 'people',
  'gesture-tracking': 'cursor',
  'photography': 'camera',
  'devops': 'gitbranch',
  // Imageless jobs use a glyph instead of a logo
  'side-projects': 'rocket',
};

function NodeGlyph({ id, radius }) {
  const key = GLYPH_FOR[id];
  if (!key || !GLYPHS[key]) return null;
  // Glyphs are drawn at ~12-unit half-extent. Scale to ~62% of node radius.
  const scale = (radius * 0.62) / 12;
  return (
    <g className="hg-glyph" transform={`scale(${scale.toFixed(3)})`}>
      {GLYPHS[key]}
    </g>
  );
}

// ── Tiny seeded RNG so layouts are stable across reloads ────────────────────
function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Component ───────────────────────────────────────────────────────────────
export default function HistoryGraph() {
  const { byId, adj, edges } = useMemo(
    () => buildIndex(graphData.nodes, graphData.edges),
    []
  );

  // Per-node degree, radius, role-classification
  const nodeMeta = useMemo(() => {
    const m = new Map();
    graphData.nodes.forEach(n => {
      const degree = adj.get(n.id).size;
      m.set(n.id, { degree, radius: nodeRadius(n, degree) });
    });
    return m;
  }, [adj]);

  // Multi-source BFS from CLUSTER_HUBS so every node knows which colour band
  // it belongs to. Hubs are seeded simultaneously and the first to reach a
  // node claims it — guarantees every node gets exactly one cluster.
  const clusterOf = useMemo(() => {
    const m = new Map();
    const present = CLUSTER_HUBS.filter(h => byId.has(h));
    present.forEach(h => m.set(h, h));
    let frontier = [...present];
    while (frontier.length) {
      const next = [];
      frontier.forEach(id => {
        adj.get(id)?.forEach(nb => {
          if (!m.has(nb)) {
            m.set(nb, m.get(id));
            next.push(nb);
          }
        });
      });
      frontier = next;
    }
    return m;
  }, [adj, byId]);

  // Refs that drive the simulation imperatively (no per-frame React render)
  const svgRef = useRef(null);
  const worldRef = useRef(null);             // <g> we pan/zoom
  const nodeElRefs = useRef(new Map());      // id -> <g> for the node
  const edgeElRefs = useRef([]);             // index-aligned with `edges`
  const pos = useRef(new Map());             // id -> { x, y, vx, vy, fx?, fy? }
  const alpha = useRef(1);
  const rafRef = useRef(0);
  const sizeRef = useRef({ w: 1200, h: 800 });
  const reduceMotionRef = useRef(false);

  // Viewport (pan/zoom) — kept out of state to avoid re-renders during pan
  const viewRef = useRef({ x: 0, y: 0, k: 1 });

  // Interaction state. `pinnedId` survives until the user pans/taps empty
  // space — that's what makes a tap "show all connecting nodes" without
  // them disappearing the moment your finger leaves the node. `hoveredId`
  // is mouse-only ephemeral preview.
  const [hoveredId, setHoveredId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // Coarse pointer / narrow viewport → no detail panel; the highlight
  // already carries everything useful and the panel covers the graph.
  const [isCoarse, setIsCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse), (max-width: 720px)');
    setIsCoarse(mq.matches);
    const handler = (e) => setIsCoarse(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  // Visible-type filter — Set so toggling is fast. All on by default.
  const [visibleTypes, setVisibleTypes] = useState(() => new Set(['job', 'skill', 'tool']));
  const toggleType = useCallback((t) => {
    setVisibleTypes(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      // Always keep at least one type on so the canvas isn't empty
      if (next.size === 0) return prev;
      return next;
    });
    alpha.current = Math.max(alpha.current, ALPHA_KICK);
  }, []);

  const visibleNodes = useMemo(
    () => graphData.nodes.filter(n => visibleTypes.has(n.type)),
    [visibleTypes]
  );
  const visibleNodeIds = useMemo(
    () => new Set(visibleNodes.map(n => n.id)),
    [visibleNodes]
  );
  const visibleEdges = useMemo(
    () => edges.filter(([a, b]) => visibleNodeIds.has(a) && visibleNodeIds.has(b)),
    [edges, visibleNodeIds]
  );

  // Track pointer drag of nodes / canvas
  const dragRef = useRef(null); // { kind: 'node'|'pan', id?, pointerId, startWX, startWY, startVX, startVY }

  // Distance from CORE for every node — used to drive a hierarchical
  // highlight (walk upward via the lowest-depth neighbour, walk down into
  // every deeper neighbour). Computed once per adjacency change.
  const depthFromCore = useMemo(() => {
    const m = new Map();
    if (!byId.has('real-time-3d')) return m;
    m.set('real-time-3d', 0);
    let frontier = ['real-time-3d'];
    let depth = 1;
    while (frontier.length) {
      const next = [];
      frontier.forEach(id => {
        adj.get(id)?.forEach(nb => {
          if (!m.has(nb)) {
            m.set(nb, depth);
            next.push(nb);
          }
        });
      });
      frontier = next;
      depth++;
    }
    return m;
  }, [adj, byId]);

  // Hierarchical highlight: ancestors from the focused node up to CORE
  // (one chain, picking the lowest-depth neighbour at each step) PLUS
  // every descendant in the focused node's subtree. Reads as "this node
  // and where it sits under CORE".
  const hierarchySet = useCallback((rootId) => {
    if (!rootId) return null;
    const visited = new Set([rootId]);

    // Walk upward toward CORE
    let cur = rootId;
    let curDepth = depthFromCore.get(cur);
    while (curDepth != null && curDepth > 0) {
      let parent = null;
      let parentDepth = curDepth;
      adj.get(cur)?.forEach(nb => {
        const d = depthFromCore.get(nb);
        if (d != null && d < parentDepth) {
          parent = nb;
          parentDepth = d;
        }
      });
      if (!parent) break;
      visited.add(parent);
      cur = parent;
      curDepth = parentDepth;
    }

    // Walk downward — every neighbour strictly deeper than its predecessor
    let frontier = [rootId];
    while (frontier.length) {
      const next = [];
      frontier.forEach(id => {
        const idDepth = depthFromCore.get(id);
        adj.get(id)?.forEach(nb => {
          if (visited.has(nb)) return;
          const d = depthFromCore.get(nb);
          if (d != null && idDepth != null && d > idDepth) {
            visited.add(nb);
            next.push(nb);
          }
        });
      });
      frontier = next;
    }

    return visited;
  }, [adj, depthFromCore]);

  // ── Initialise positions once ───────────────────────────────────────────
  // Topology-aware seed: pin every cluster hub at its SKILL_PINS slot, then
  // BFS outward from those hubs assigning each non-hub node to its closest
  // hub. Each non-hub node is then placed on a depth-based ring around its
  // primary hub, so cluster siblings start near each other and physics has
  // very little detangling left to do.
  useEffect(() => {
    const rng = mulberry32(0xC1A551C);

    // 1. Pin all cluster hubs from SKILL_PINS.
    const hubIds = CLUSTER_HUBS.filter(h => byId.has(h));
    const hubPos = new Map();
    hubIds.forEach((id, i) => {
      const pin = SKILL_PINS[id] ?? {
        // Fallback: ring around CORE if no explicit pin
        x: Math.cos((i / hubIds.length) * Math.PI * 2) * 360,
        y: Math.sin((i / hubIds.length) * Math.PI * 2) * 360,
      };
      hubPos.set(id, pin);
      pos.current.set(id, {
        x: pin.x, y: pin.y, vx: 0, vy: 0,
        fx: pin.x, fy: pin.y,
      });
    });
    const hubSet = new Set(hubIds);

    // 2. Multi-source BFS from hubs → each non-hub gets its closest hub.
    const primaryHub = new Map();
    const hubDist = new Map();
    hubIds.forEach(h => { primaryHub.set(h, h); hubDist.set(h, 0); });
    let frontier = [...hubIds];
    let depth = 1;
    while (frontier.length) {
      const next = [];
      frontier.forEach(id => {
        adj.get(id)?.forEach(nb => {
          if (!primaryHub.has(nb)) {
            primaryHub.set(nb, primaryHub.get(id));
            hubDist.set(nb, depth);
            next.push(nb);
          }
        });
      });
      frontier = next;
      depth++;
    }

    // 3. Group non-hubs by primary hub.
    const byHub = new Map();
    graphData.nodes.forEach(n => {
      if (hubSet.has(n.id)) return; // hubs already placed
      const h = primaryHub.get(n.id);
      if (!h) {
        // Truly disconnected — small jitter near centre.
        pos.current.set(n.id, {
          x: (rng() - 0.5) * 120,
          y: (rng() - 0.5) * 120,
          vx: 0, vy: 0,
        });
        return;
      }
      if (!byHub.has(h)) byHub.set(h, []);
      byHub.get(h).push(n);
    });

    // 4. Place each cluster's members on depth-based rings around their hub.
    //    Ring radius scales gently with cluster size so big clusters don't
    //    pack into a ring too tight for them.
    byHub.forEach((members, hubId) => {
      const { x: hx, y: hy } = hubPos.get(hubId);
      members.sort((a, b) => (hubDist.get(a.id) - hubDist.get(b.id)));
      const sizeBoost = Math.max(0, members.length - 6) * 2.5;
      members.forEach((n, i) => {
        const d = hubDist.get(n.id) || 1;
        const baseR = 60 + sizeBoost + (d - 1) * 42;
        const angle = (i / members.length) * Math.PI * 2 + (rng() - 0.5) * 0.4;
        const r = baseR + rng() * 18;
        pos.current.set(n.id, {
          x: hx + Math.cos(angle) * r,
          y: hy + Math.sin(angle) * r,
          vx: 0, vy: 0,
        });
      });
    });
  }, [adj, byId]);

  // ── ResizeObserver for full-bleed canvas ─────────────────────────────────
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = () => {
      const rect = svg.getBoundingClientRect();
      sizeRef.current = { w: rect.width, h: rect.height };
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(svg);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // ── Measure the sticky nav so the header sits flush beneath it ──────────
  useEffect(() => {
    const nav = document.querySelector('.site-nav');
    if (!nav) return;
    const setH = () => {
      const h = nav.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--hg-nav-h', `${h}px`);
    };
    setH();
    const ro = new ResizeObserver(setH);
    ro.observe(nav);
    window.addEventListener('resize', setH);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', setH);
      document.documentElement.style.removeProperty('--hg-nav-h');
    };
  }, []);

  // ── prefers-reduced-motion ──────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduceMotionRef.current = mq.matches;
    const handler = (e) => { reduceMotionRef.current = e.matches; };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Force tick ───────────────────────────────────────────────────────────
  const tick = useCallback(() => {
    const nodes = visibleNodes;
    const radii = nodes.map(n => nodeMeta.get(n.id).radius);

    // Pairwise repulsion — type-aware so jobs spread wide
    for (let i = 0; i < nodes.length; i++) {
      const a = pos.current.get(nodes[i].id);
      const aType = nodes[i].type;
      for (let j = i + 1; j < nodes.length; j++) {
        const b = pos.current.get(nodes[j].id);
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) { d2 = 1; dx = 0.5; dy = 0.5; }
        const mult = REPULSE_PAIR[pairKey(aType, nodes[j].type)] ?? 1;
        const f = (REPULSE_STRENGTH * mult) / d2;
        const d = Math.sqrt(d2);
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    // Spring forces along edges (visible only)
    for (let i = 0; i < visibleEdges.length; i++) {
      const [aId, bId] = visibleEdges[i];
      const a = pos.current.get(aId);
      const b = pos.current.get(bId);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const delta = (d - EDGE_DISTANCE) * EDGE_STIFFNESS;
      const fx = (dx / d) * delta;
      const fy = (dy / d) * delta;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    // Cluster gathering — pull each node toward its cluster's centroid so
    // same-coloured nodes physically group together. Centroids include
    // pinned hubs so each cluster's gravity is anchored where its hub sits.
    const centroids = new Map();
    const counts = new Map();
    for (const n of nodes) {
      const c = clusterOf.get(n.id);
      if (!c) continue;
      const p = pos.current.get(n.id);
      const sum = centroids.get(c);
      if (sum) { sum.x += p.x; sum.y += p.y; }
      else centroids.set(c, { x: p.x, y: p.y });
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    centroids.forEach((sum, c) => {
      const k = counts.get(c);
      sum.x /= k; sum.y /= k;
    });
    const CLUSTER_PULL = 0.005;
    for (const n of nodes) {
      const p = pos.current.get(n.id);
      if (p.fx != null) continue;
      const c = clusterOf.get(n.id);
      if (!c) continue;
      const cent = centroids.get(c);
      p.vx += (cent.x - p.x) * CLUSTER_PULL;
      p.vy += (cent.y - p.y) * CLUSTER_PULL;
    }

    // Centre pull — subtle and always-on (NOT alpha-scaled). A gentle leash
    // keeping everything roughly within the canvas without bunching on tap.
    for (const n of nodes) {
      const p = pos.current.get(n.id);
      if (p.fx != null) {
        p.x = p.fx; p.y = p.fy;
        p.vx = 0; p.vy = 0;
        continue;
      }
      p.vx -= p.x * CENTRE_PULL;
      p.vy -= p.y * CENTRE_PULL;
      p.vx *= DAMPING;
      p.vy *= DAMPING;
      p.x += p.vx;
      p.y += p.vy;
    }

    // Collision resolution — Verlet-style position correction so node circles
    // never overlap. A few iterations stabilise even densely connected pairs.
    for (let iter = 0; iter < COLLISION_ITERS; iter++) {
      for (let i = 0; i < nodes.length; i++) {
        const a = pos.current.get(nodes[i].id);
        const ra = radii[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = pos.current.get(nodes[j].id);
          const minDist = ra + radii[j] + COLLISION_BUFFER;
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          let d2 = dx * dx + dy * dy;
          const md2 = minDist * minDist;
          if (d2 < md2 && d2 > 0) {
            const d = Math.sqrt(d2);
            const overlap = (minDist - d) * 0.5;
            const nx = dx / d;
            const ny = dy / d;
            if (a.fx == null) { a.x -= nx * overlap; a.y -= ny * overlap; }
            if (b.fx == null) { b.x += nx * overlap; b.y += ny * overlap; }
          }
        }
      }
    }
  }, [visibleNodes, visibleEdges, nodeMeta, clusterOf]);

  // ── Paint positions to DOM ───────────────────────────────────────────────
  const paint = useCallback(() => {
    nodeElRefs.current.forEach((el, id) => {
      const p = pos.current.get(id);
      if (!p || !el) return;
      el.setAttribute('transform', `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)})`);
    });
    visibleEdges.forEach((e, i) => {
      const el = edgeElRefs.current[i];
      if (!el) return;
      const a = pos.current.get(e[0]);
      const b = pos.current.get(e[1]);
      el.setAttribute('x1', a.x.toFixed(2));
      el.setAttribute('y1', a.y.toFixed(2));
      el.setAttribute('x2', b.x.toFixed(2));
      el.setAttribute('y2', b.y.toFixed(2));
    });
  }, [visibleEdges]);

  // ── Animation loop ───────────────────────────────────────────────────────
  useEffect(() => {
    let stopped = false;
    let settledOnce = false;

    // Pre-settle: run a high-alpha burst before the first paint so the layout
    // appears already-converged. Saves the user from watching it self-organise
    // and is also re-run on filter changes for a snappy re-layout.
    alpha.current = 1.2;
    for (let k = 0; k < SETTLE_ITERATIONS; k++) tick();
    alpha.current = ALPHA_MIN;
    paint();

    const loop = () => {
      if (stopped) return;
      tick();
      paint();
      // Decay temperature, but never below ALPHA_MIN so the graph keeps breathing
      alpha.current = Math.max(ALPHA_MIN, alpha.current - ALPHA_DECAY);

      // Reduced-motion: settle once and stop
      if (reduceMotionRef.current) {
        if (!settledOnce && alpha.current <= ALPHA_MIN + 0.001) {
          settledOnce = true;
          // run a few more ticks to fully settle
          for (let k = 0; k < 60; k++) tick();
          paint();
          return;
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      stopped = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [tick, paint]);

  // ── Pan/zoom application (writes transform on the world group) ───────────
  const applyView = useCallback(() => {
    const v = viewRef.current;
    if (worldRef.current) {
      worldRef.current.setAttribute('transform', `translate(${v.x} ${v.y}) scale(${v.k})`);
    }
  }, []);
  useEffect(() => { applyView(); }, [applyView]);

  // ── Convert client (px) → world (graph) coords ───────────────────────────
  // The SVG uses preserveAspectRatio="xMidYMid meet" with a 1200×800 viewBox,
  // so 1 client pixel ≠ 1 world unit unless the screen happens to match the
  // viewBox aspect ratio. On phones the viewBox is shrunk hard (e.g. 0.33×),
  // so dragging without dividing by the meet scale produced visibly less
  // node movement than finger movement — the "janky / not 1:1" complaint.
  const VIEWBOX_W = 1640;
  const VIEWBOX_H = 800;
  const clientToWorld = useCallback((cx, cy) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const v = viewRef.current;
    const meet = Math.min(rect.width / VIEWBOX_W, rect.height / VIEWBOX_H) || 1;
    const sx = (cx - rect.left - rect.width / 2) / meet;
    const sy = (cy - rect.top - rect.height / 2) / meet;
    return { x: (sx - v.x) / v.k, y: (sy - v.y) / v.k };
  }, []);

  const kickAlpha = useCallback(() => {
    alpha.current = Math.max(alpha.current, ALPHA_KICK);
  }, []);

  // ── Pointer interactions (multi-touch aware) ─────────────────────────────
  // activePtrs tracks every pointer currently down so we can do pinch-to-zoom.
  // dragRef holds the current interaction mode ('node' | 'pan' | 'pinch').
  const activePtrs = useRef(new Map()); // pointerId → { clientX, clientY, pointerType }

  // Touch fingers wobble more than mouse cursors during a tap; raise the
  // "is this a drag yet" threshold so a real tap reliably opens the panel.
  const tapThreshold = (pointerType) => (pointerType === 'touch' ? 10 : 4);

  const enterPinch = useCallback(() => {
    if (activePtrs.current.size < 2) return;
    const ids = Array.from(activePtrs.current.keys()).slice(0, 2);
    const p1 = activePtrs.current.get(ids[0]);
    const p2 = activePtrs.current.get(ids[1]);
    const dist = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
    const midCX = (p1.clientX + p2.clientX) / 2;
    const midCY = (p1.clientY + p2.clientY) / 2;
    const v = viewRef.current;
    // Drop any prior single-pointer mode; a node drag can't continue as
    // pinch, so release its fixed position cleanly.
    const prior = dragRef.current;
    if (prior && prior.kind === 'node') {
      const p = pos.current.get(prior.id);
      const node = byId.get(prior.id);
      if (node?.type !== 'job') { p.fx = null; p.fy = null; }
    }
    dragRef.current = {
      kind: 'pinch',
      p1Id: ids[0],
      p2Id: ids[1],
      startDist: Math.max(dist, 1),
      startK: v.k,
      startVX: v.x,
      startVY: v.y,
      startMidCX: midCX,
      startMidCY: midCY,
    };
  }, [byId]);

  const onNodePointerDown = useCallback((e, id) => {
    e.stopPropagation();
    activePtrs.current.set(e.pointerId, {
      clientX: e.clientX, clientY: e.clientY, pointerType: e.pointerType,
    });
    if (activePtrs.current.size >= 2) {
      enterPinch();
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const w = clientToWorld(e.clientX, e.clientY);
    const p = pos.current.get(id);
    p.fx = p.x; p.fy = p.y;
    dragRef.current = {
      kind: 'node',
      id,
      pointerId: e.pointerId,
      grabDX: w.x - p.x,
      grabDY: w.y - p.y,
      startCX: e.clientX,
      startCY: e.clientY,
      pointerType: e.pointerType,
      moved: false,
    };
    // Don't kick alpha on press — onPointerMove kicks it once a real drag
    // is detected. Press-only events would otherwise bump the centre-pull
    // and visibly suck the layout inward on every tap.
  }, [clientToWorld, enterPinch]);

  const onSvgPointerDown = useCallback((e) => {
    // Node handler stops propagation, so this only fires for background.
    activePtrs.current.set(e.pointerId, {
      clientX: e.clientX, clientY: e.clientY, pointerType: e.pointerType,
    });
    if (activePtrs.current.size >= 2) {
      enterPinch();
      return;
    }
    const v = viewRef.current;
    dragRef.current = {
      kind: 'pan',
      pointerId: e.pointerId,
      startCX: e.clientX,
      startCY: e.clientY,
      startVX: v.x,
      startVY: v.y,
      pointerType: e.pointerType,
      moved: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [enterPinch]);

  const onPointerMove = useCallback((e) => {
    if (activePtrs.current.has(e.pointerId)) {
      activePtrs.current.set(e.pointerId, {
        clientX: e.clientX, clientY: e.clientY, pointerType: e.pointerType,
      });
    }
    const d = dragRef.current;
    if (!d) return;

    const svg = svgRef.current;
    const rect = svg ? svg.getBoundingClientRect() : null;
    const meet = rect
      ? (Math.min(rect.width / VIEWBOX_W, rect.height / VIEWBOX_H) || 1)
      : 1;

    if (d.kind === 'pinch') {
      const p1 = activePtrs.current.get(d.p1Id);
      const p2 = activePtrs.current.get(d.p2Id);
      if (!p1 || !p2 || !rect) return;
      const newDist = Math.max(
        Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY),
        1
      );
      const newK = Math.max(0.35, Math.min(3, d.startK * (newDist / d.startDist)));
      const realRatio = newK / d.startK;
      // Anchor zoom around the original two-finger midpoint, in viewBox units.
      const ax = (d.startMidCX - rect.left - rect.width / 2) / meet;
      const ay = (d.startMidCY - rect.top - rect.height / 2) / meet;
      let newVX = ax - (ax - d.startVX) * realRatio;
      let newVY = ay - (ay - d.startVY) * realRatio;
      // Plus the two-finger pan component (midpoint travel), client→viewBox.
      const newMidCX = (p1.clientX + p2.clientX) / 2;
      const newMidCY = (p1.clientY + p2.clientY) / 2;
      newVX += (newMidCX - d.startMidCX) / meet;
      newVY += (newMidCY - d.startMidCY) / meet;
      const v = viewRef.current;
      v.x = newVX; v.y = newVY; v.k = newK;
      applyView();
      return;
    }

    if (d.pointerId !== e.pointerId) return;

    if (d.kind === 'node') {
      const w = clientToWorld(e.clientX, e.clientY);
      const p = pos.current.get(d.id);
      p.fx = w.x - d.grabDX;
      p.fy = w.y - d.grabDY;
      if (!d.moved) {
        const dx = e.clientX - d.startCX;
        const dy = e.clientY - d.startCY;
        if (Math.hypot(dx, dy) > tapThreshold(d.pointerType)) d.moved = true;
      }
      kickAlpha();
    } else if (d.kind === 'pan') {
      const v = viewRef.current;
      // viewRef stores translate values in viewBox units, so client deltas
      // need to be divided by the meet scale.
      v.x = d.startVX + (e.clientX - d.startCX) / meet;
      v.y = d.startVY + (e.clientY - d.startCY) / meet;
      if (!d.moved) {
        const dx = e.clientX - d.startCX;
        const dy = e.clientY - d.startCY;
        if (Math.hypot(dx, dy) > tapThreshold(d.pointerType)) d.moved = true;
      }
      applyView();
    }
  }, [clientToWorld, applyView, kickAlpha]);

  const onPointerUp = useCallback((e) => {
    activePtrs.current.delete(e.pointerId);
    const d = dragRef.current;
    if (!d) return;

    if (d.kind === 'pinch') {
      if (e.pointerId !== d.p1Id && e.pointerId !== d.p2Id) return;
      // If one finger remains, downgrade to a pan so the user can keep moving
      // around without lifting and re-pressing.
      if (activePtrs.current.size === 1) {
        const remainingId = activePtrs.current.keys().next().value;
        const rp = activePtrs.current.get(remainingId);
        const v = viewRef.current;
        dragRef.current = {
          kind: 'pan',
          pointerId: remainingId,
          startCX: rp.clientX,
          startCY: rp.clientY,
          startVX: v.x,
          startVY: v.y,
          pointerType: rp.pointerType,
          moved: true,        // already past the tap threshold
        };
      } else {
        dragRef.current = null;
      }
      return;
    }

    if (d.pointerId !== e.pointerId) return;
    if (d.kind === 'node') {
      const p = pos.current.get(d.id);
      const node = byId.get(d.id);
      // Jobs stay pinned at the dragged location (anchors of the timeline).
      // Skills/tools release their fixed position and keep momentum.
      if (node?.type !== 'job') {
        p.fx = null; p.fy = null;
      }
      if (!d.moved) {
        // Tap → pin the 2-hop highlight so the connecting hierarchy stays
        // visible. Panel only opens for jobs on non-coarse pointers, since
        // skill/tool nodes have no extra info beyond their connections,
        // and on mobile the panel covers the very graph the user wants
        // to study.
        setPinnedId(d.id);
        if (node?.type === 'job' && !isCoarse) {
          setSelectedId(d.id);
          setPanelOpen(true);
        } else {
          setPanelOpen(false);
        }
      }
    } else if (d.kind === 'pan') {
      if (!d.moved) {
        setPanelOpen(false);
        setPinnedId(null);
        setHoveredId(null);
      }
    }
    dragRef.current = null;
  }, [byId, isCoarse]);

  // Wheel zoom (centred on cursor)
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const meet = Math.min(rect.width / VIEWBOX_W, rect.height / VIEWBOX_H) || 1;
    const cx = (e.clientX - rect.left - rect.width / 2) / meet;
    const cy = (e.clientY - rect.top - rect.height / 2) / meet;
    const v = viewRef.current;
    const factor = Math.exp(-e.deltaY * 0.0015);
    const newK = Math.max(0.35, Math.min(3, v.k * factor));
    // Keep cursor anchor stable: world point under cursor before == after.
    // (cx - v.x) / v.k = (cx - v.x') / newK  →  v.x' = cx - (cx - v.x) * (newK / v.k)
    const ratio = newK / v.k;
    v.x = cx - (cx - v.x) * ratio;
    v.y = cy - (cy - v.y) * ratio;
    v.k = newK;
    applyView();
  }, [applyView]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // ── Hover (mouse only — touch users open the panel via click) ────────────
  const onNodePointerEnter = useCallback((e, id) => {
    if (e.pointerType === 'touch') return;
    setHoveredId(id);
  }, []);
  const onNodePointerLeave = useCallback((e) => {
    if (e && e.pointerType === 'touch') return;
    setHoveredId(null);
  }, []);

  // Highlight set: 2-hop hierarchy from whichever id is currently focused.
  // Hover (ephemeral, mouse) wins over pinned (sticky tap) so moving the
  // mouse over another node previews its hierarchy without losing the pin.
  const highlightSet = useMemo(
    () => hierarchySet(hoveredId || pinnedId),
    [hoveredId, pinnedId, hierarchySet]
  );

  // ── Keyboard: Enter pins the highlight; opens panel for jobs on desktop. ─
  const onNodeKeyDown = useCallback((e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setPinnedId(id);
      const node = byId.get(id);
      if (node?.type === 'job' && !isCoarse) {
        setSelectedId(id);
        setPanelOpen(true);
      }
    }
  }, [byId, isCoarse]);

  // ── Side-panel content for selected node ─────────────────────────────────
  const selectedNode = selectedId ? byId.get(selectedId) : null;
  const selectedNeighbours = useMemo(() => {
    if (!selectedId) return { jobs: [], skills: [], tools: [] };
    const out = { jobs: [], skills: [], tools: [] };
    adj.get(selectedId)?.forEach(id => {
      const n = byId.get(id);
      if (!n) return;
      if (n.type === 'job') out.jobs.push(n);
      else if (n.type === 'skill') out.skills.push(n);
      else if (n.type === 'tool') out.tools.push(n);
    });
    return out;
  }, [selectedId, adj, byId]);

  return (
    <div className="hg-root">
      <header className="hg-header">
        <h1 className="hg-title">Experience</h1>
        <p className="hg-sub">Jobs, skills, and tools — tap to see how they connect.</p>
      </header>

      <div className="hg-filters" role="group" aria-label="Filter graph by node type">
        {[
          { key: 'job', label: 'Jobs' },
          { key: 'skill', label: 'Skills' },
          { key: 'tool', label: 'Tools' },
        ].map(({ key, label }) => {
          const on = visibleTypes.has(key);
          return (
            <button
              key={key}
              type="button"
              className={`hg-filter hg-filter--${key}${on ? ' is-on' : ''}`}
              aria-pressed={on}
              onClick={() => toggleType(key)}
            >
              <span className="hg-filter-dot" />
              {label}
            </button>
          );
        })}
      </div>

      <svg
        ref={svgRef}
        className={`hg-svg${highlightSet ? ' is-focusing' : ''}`}
        onPointerDown={onSvgPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        viewBox="-820 -400 1640 800"
        preserveAspectRatio="xMidYMid meet"
      >
        <g ref={worldRef} className="hg-world">
          {/* Edges first so they render under nodes */}
          <g className="hg-edges">
            {visibleEdges.map(([a, b], i) => {
              const dim = highlightSet && !(highlightSet.has(a) && highlightSet.has(b));
              return (
                <line
                  key={`${a}--${b}`}
                  ref={el => { edgeElRefs.current[i] = el; }}
                  className={`hg-edge${dim ? ' is-dim' : ''}`}
                />
              );
            })}
          </g>

          {/* Nodes */}
          <g className="hg-nodes">
            {visibleNodes.map(n => {
              const meta = nodeMeta.get(n.id);
              const r = meta.radius;
              const dim = highlightSet && !highlightSet.has(n.id);
              const isSel = pinnedId === n.id || selectedId === n.id;
              const clusterId = clusterOf.get(n.id);
              const clusterColor = clusterId ? CLUSTER_COLORS[clusterId] : null;
              return (
                <g
                  key={n.id}
                  ref={el => {
                    if (el) nodeElRefs.current.set(n.id, el);
                    else nodeElRefs.current.delete(n.id);
                  }}
                  className={`hg-node hg-node--${n.type}${dim ? ' is-dim' : ''}${isSel ? ' is-selected' : ''}`}
                  style={clusterColor ? { '--cluster-color': clusterColor } : undefined}
                  onPointerDown={e => onNodePointerDown(e, n.id)}
                  onPointerEnter={e => onNodePointerEnter(e, n.id)}
                  onPointerLeave={onNodePointerLeave}
                  onKeyDown={e => onNodeKeyDown(e, n.id)}
                  tabIndex={0}
                  role="button"
                  aria-label={n.label}
                >
                  {n.type === 'job' ? (
                    <>
                      <circle r={r} className="hg-node-ring" />
                      {n.image ? (
                        <>
                          <defs>
                            <clipPath id={`clip-${n.id}`}>
                              <circle r={r - 2} />
                            </clipPath>
                          </defs>
                          <image
                            href={n.image}
                            x={-r + 2} y={-r + 2}
                            width={(r - 2) * 2} height={(r - 2) * 2}
                            clipPath={`url(#clip-${n.id})`}
                            preserveAspectRatio="xMidYMid slice"
                          />
                        </>
                      ) : (
                        <NodeGlyph id={n.id} radius={r} />
                      )}
                    </>
                  ) : (
                    <>
                      <circle r={r} className="hg-node-circle" />
                      <NodeGlyph id={n.id} radius={r} />
                    </>
                  )}
                  <text
                    className="hg-node-label"
                    y={r + (n.type === 'job' ? 18 : 14)}
                    textAnchor="middle"
                  >
                    {n.label}
                  </text>
                </g>
              );
            })}
          </g>
        </g>
      </svg>

      {/* Side panel */}
      <aside
        className={`hg-panel${panelOpen ? ' is-open' : ''}`}
        aria-hidden={!panelOpen}
      >
        {selectedNode && (
          <>
            <button
              className="hg-panel-close"
              onClick={() => setPanelOpen(false)}
              aria-label="Close panel"
            >×</button>

            <div className="hg-panel-head">
              {selectedNode.type === 'job' && selectedNode.image && (
                <img className="hg-panel-logo" src={selectedNode.image} alt="" />
              )}
              <div>
                <span className={`hg-panel-kind hg-panel-kind--${selectedNode.type}`}>
                  {selectedNode.type}
                </span>
                <h2 className="hg-panel-title">{selectedNode.label}</h2>
                {selectedNode.time && (
                  <p className="hg-panel-time">{selectedNode.time}</p>
                )}
              </div>
            </div>

            {selectedNode.type === 'job' && selectedNode.roles?.length > 0 && (
              <div className="hg-panel-roles">
                {selectedNode.roles.map((role, i) => (
                  <section className="hg-role" key={i}>
                    <h3 className="hg-role-title">{role.title}</h3>
                    {role.time && <p className="hg-role-time">{role.time}</p>}
                    <ul className="hg-role-bullets">
                      {role.bullets?.map((b, j) => <li key={j}>{b}</li>)}
                    </ul>
                  </section>
                ))}
              </div>
            )}

            {selectedNode.type !== 'job' && (
              <div className="hg-panel-relations">
                {selectedNeighbours.jobs.length > 0 && (
                  <section>
                    <h3 className="hg-rel-title">Used at</h3>
                    <ul className="hg-rel-list">
                      {selectedNeighbours.jobs.map(j => (
                        <li key={j.id}>
                          <button
                            className="hg-rel-link"
                            onClick={() => setSelectedId(j.id)}
                          >{j.label}</button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {(selectedNeighbours.skills.length + selectedNeighbours.tools.length) > 0 && (
                  <section>
                    <h3 className="hg-rel-title">Related to</h3>
                    <ul className="hg-rel-list">
                      {[...selectedNeighbours.skills, ...selectedNeighbours.tools].map(n => (
                        <li key={n.id}>
                          <button
                            className="hg-rel-link"
                            onClick={() => setSelectedId(n.id)}
                          >{n.label}</button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
